import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Avatar,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import {
  Store as StoreIcon,
  Person as PersonIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { ChatListProps } from '../types/chat';
import { CHAT_CHA } from '../theme/chatChaavi';
import { VCH } from '../theme/vendorChatChaavi';

const ChatList: React.FC<ChatListProps> = ({
  chats,
  onChatSelect,
  selectedChatId,
  currentUserPhone,
  variant = 'default',
}) => {
  const isChaavi = variant === 'chaavi';
  const isVendorChaavi = variant === 'vendor-chaavi';

  const formatTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else if (diffInHours < 168) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    }
  };

  const truncateMessage = (message?: string, maxLength: number = 52) => {
    if (!message) return 'No messages yet';
    return message.length > maxLength ? `${message.substring(0, maxLength)}...` : message;
  };

  const formatVendorListTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffM = (now.getTime() - date.getTime()) / 60000;
    if (diffM < 1) return 'Just now';
    if (diffM < 60) return `${Math.floor(diffM)}m ago`;
    const diffInHours = diffM / 60;
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }
    if (diffInHours < 168) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getOtherParticipantInfo = (chat: any) => {
    const isClient = chat.clientPhone === currentUserPhone;
    return {
      phone: isClient ? chat.vendorPhone : chat.clientPhone,
      name: chat.otherParticipantName || (isClient ? 'Vendor' : 'Client'),
      type: isClient ? 'VENDOR' : 'CLIENT',
    };
  };

  if (chats.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          height: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: isVendorChaavi ? '#fff' : isChaavi ? CHAT_CHA.shellBg : undefined,
          borderRadius: 0,
          boxShadow: 'none',
        }}
      >
        <ChatIcon
          sx={{
            fontSize: 56,
            color: isVendorChaavi ? VCH.muted : isChaavi ? CHAT_CHA.goldPale : 'text.secondary',
            mb: 2,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: isVendorChaavi ? VCH.ink : isChaavi ? CHAT_CHA.text : 'text.secondary',
            fontFamily: isVendorChaavi ? 'sans-serif' : isChaavi ? '"Playfair Display", serif' : undefined,
            fontWeight: isVendorChaavi ? 500 : 400,
            mb: 1,
          }}
          gutterBottom
        >
          {isVendorChaavi ? 'No client chats yet' : 'No conversations yet'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: isVendorChaavi ? VCH.muted : isChaavi ? CHAT_CHA.textMuted : 'text.secondary',
            maxWidth: 280,
            fontFamily: isVendorChaavi ? 'sans-serif' : undefined,
          }}
        >
          {isVendorChaavi
            ? 'When clients message you about an order, conversations will appear here.'
            : 'Start a conversation with a vendor from Explore or open a new chat.'}
        </Typography>
      </Paper>
    );
  }

  if (isVendorChaavi) {
    const avatarPalettes = ['#f0ebe0', '#e8f4ed', '#f0eaff'] as const;
    return (
      <Box sx={{ height: '100%', overflow: 'auto', flex: 1, bgcolor: '#fff' }}>
        <List sx={{ p: 0 }}>
          {chats.map((chat, index) => {
            const otherParticipant = getOtherParticipantInfo(chat);
            const isSelected = selectedChatId === chat.chatId;
            const initial = (otherParticipant.name || 'C').charAt(0).toUpperCase();
            const avatarBg = avatarPalettes[Math.abs(chat.chatId) % avatarPalettes.length];
            const hasUnread = Boolean(chat.unreadCount && chat.unreadCount > 0);

            return (
              <React.Fragment key={chat.chatId}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => onChatSelect(chat)}
                    sx={{
                      py: 1.25,
                      px: 1.75,
                      alignItems: 'flex-start',
                      gap: 1.25,
                      bgcolor: isSelected ? VCH.convActiveBg : 'transparent',
                      borderLeft: isSelected ? `3px solid ${VCH.accent}` : '3px solid transparent',
                      '&:hover': { bgcolor: isSelected ? VCH.convActiveBg : VCH.convHoverBg },
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 44, mt: 0.25 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          fontSize: 15,
                          fontWeight: 600,
                          bgcolor: avatarBg,
                          color: VCH.barMuted,
                          fontFamily: 'sans-serif',
                        }}
                      >
                        {initial}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      disableTypography
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={0.75}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: hasUnread ? 600 : 500,
                              color: VCH.ink,
                              fontFamily: 'sans-serif',
                              flex: 1,
                              minWidth: 0,
                            }}
                            noWrap
                          >
                            {otherParticipant.name}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 10, color: VCH.dateMuted, flexShrink: 0, fontFamily: 'sans-serif' }}
                          >
                            {formatVendorListTime(chat.lastMessageTime)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                          <Typography
                            component="span"
                            display="block"
                            noWrap
                            sx={{
                              fontSize: 12,
                              color: VCH.muted,
                              fontFamily: 'sans-serif',
                              lineHeight: 1.45,
                            }}
                          >
                            {truncateMessage(chat.lastMessage, 48)}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                            <Typography
                              component="span"
                              noWrap
                              sx={{ fontSize: 10, color: VCH.dateMuted, fontFamily: 'sans-serif', flex: 1, minWidth: 0 }}
                            >
                              {chat.businessName}
                            </Typography>
                            {hasUnread && (
                              <Box
                                component="span"
                                sx={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: '50%',
                                  bgcolor: VCH.dotUnread,
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {index < chats.length - 1 ? (
                  <Box sx={{ height: 1, bgcolor: VCH.rule, mx: 2 }} />
                ) : null}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto', flex: 1, bgcolor: isChaavi ? CHAT_CHA.shellBg : undefined }}>
      <List sx={{ p: 0 }}>
        {chats.map((chat, index) => {
          const otherParticipant = getOtherParticipantInfo(chat);
          const isSelected = selectedChatId === chat.chatId;

          return (
            <React.Fragment key={chat.chatId}>
              <ListItem
                disablePadding
                sx={{
                  borderLeft: isChaavi && isSelected ? `4px solid ${CHAT_CHA.gold}` : '4px solid transparent',
                  bgcolor: isChaavi
                    ? isSelected
                      ? CHAT_CHA.selectedBg
                      : 'transparent'
                    : isSelected
                      ? 'primary.light'
                      : 'transparent',
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    bgcolor: isChaavi
                      ? isSelected
                        ? CHAT_CHA.selectedBg
                        : CHAT_CHA.goldMuted
                      : isSelected
                        ? 'primary.light'
                        : 'action.hover',
                  },
                }}
              >
                <ListItemButton
                  onClick={() => onChatSelect(chat)}
                  sx={{
                    py: 2,
                    px: 2,
                    alignItems: 'flex-start',
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 56 }}>
                    <Avatar
                      sx={{
                        bgcolor: isChaavi
                          ? CHAT_CHA.gold
                          : otherParticipant.type === 'VENDOR'
                            ? 'secondary.main'
                            : 'primary.main',
                        color: isChaavi ? '#fff' : undefined,
                        width: 44,
                        height: 44,
                      }}
                    >
                      {otherParticipant.type === 'VENDOR' ? <StoreIcon /> : <PersonIcon />}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={chat.unreadCount && chat.unreadCount > 0 ? 700 : 600}
                          noWrap
                          sx={{
                            flex: 1,
                            color: isChaavi ? CHAT_CHA.text : undefined,
                            fontFamily: isChaavi ? '"Jost", sans-serif' : undefined,
                          }}
                        >
                          {otherParticipant.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isChaavi ? CHAT_CHA.textMuted : undefined,
                            flexShrink: 0,
                            fontFamily: isChaavi ? '"Jost", sans-serif' : undefined,
                          }}
                        >
                          {formatTime(chat.lastMessageTime)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'block' }}>
                        <Typography
                          variant="body2"
                          component="span"
                          display="block"
                          noWrap
                          sx={{
                            color: isChaavi ? CHAT_CHA.textMuted : 'text.secondary',
                            fontWeight: chat.unreadCount && chat.unreadCount > 0 ? 600 : 400,
                            mb: 0.75,
                            mt: 0.25,
                            fontFamily: isChaavi ? '"Jost", sans-serif' : undefined,
                          }}
                        >
                          {truncateMessage(chat.lastMessage)}
                        </Typography>

                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                          <Typography
                            variant="caption"
                            component="span"
                            noWrap
                            sx={{
                              color: isChaavi ? CHAT_CHA.textMuted : 'text.secondary',
                              fontStyle: 'italic',
                              fontFamily: isChaavi ? '"Jost", sans-serif' : undefined,
                            }}
                          >
                            {chat.businessName}
                          </Typography>

                          <Box display="flex" gap={0.75} alignItems="center" flexShrink={0}>
                            {chat.orderId && (
                              <Chip
                                label={`#${chat.orderId}`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '0.68rem',
                                  height: 22,
                                  borderColor: isChaavi ? CHAT_CHA.border : undefined,
                                  color: isChaavi ? CHAT_CHA.goldDark : undefined,
                                }}
                              />
                            )}

                            {chat.unreadCount && chat.unreadCount > 0 && (
                              <Chip
                                label={chat.unreadCount}
                                size="small"
                                sx={{
                                  fontSize: '0.68rem',
                                  height: 22,
                                  minWidth: 22,
                                  bgcolor: CHAT_CHA.gold,
                                  color: '#fff',
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
              </ListItem>

              {index < chats.length - 1 &&
                (isChaavi ? (
                  <Box sx={{ height: 1, bgcolor: CHAT_CHA.border, opacity: 0.55, mx: 2 }} />
                ) : (
                  <Divider />
                ))}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default ChatList;
